import { FC } from "react";

import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { formatPrice } from "@testavivadk/utilities";

import { setCartSidebarOpen } from "@store/slices/cartSlice";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Fab from "@mui/material/Fab";
import Typography from "@mui/material/Typography";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";

import { brandColorTokens } from "@constants";
import { applicationSpaRoutes } from "@shared/constants/routes";

import { ICartFooterProperties } from "./CartFooter.types";

const CartFooter: FC<ICartFooterProperties> = ({
  totalPrice,
  partnerSaving,
  partnerName,
  checkoutUrl,
  isScrolledToBottom,
  onScrollDown,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const subtotal = totalPrice + partnerSaving;
  const isPartnerSavingShown = partnerSaving > 0 && partnerName != null;

  const handleShopMore = () => {
    dispatch(setCartSidebarOpen(false));
    navigate(applicationSpaRoutes.createDocument);
  };

  return (
    <Box
      sx={{
        position: "sticky",
        bottom: 0,
        zIndex: 1,
        mt: "auto",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        p: 3,
        bgcolor: "background.paper",
        borderTop: 1,
        borderColor: "divider",
      }}
    >
      {!isScrolledToBottom && (
        <Fab
          size="small"
          color="secondary"
          onClick={onScrollDown}
          sx={{
            position: "absolute",
            top: -56,
            left: "50%",
            transform: "translateX(-50%)",
            animation: "cartScrollNudge 2s ease-in-out infinite",
            "@keyframes cartScrollNudge": {
              "0%, 100%": { transform: "translateX(-50%) translateY(0)" },
              "50%": { transform: "translateX(-50%) translateY(6px)" },
            },
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        >
          <KeyboardArrowDownIcon />
        </Fab>
      )}
      {isPartnerSavingShown && (
        <>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {t("cartSidebar.subtotal")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: "tabular-nums" }}>
                {formatPrice(subtotal)}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
                color: brandColorTokens.status.success,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <SellOutlinedIcon fontSize="small" />
                <Typography variant="body2" fontWeight={500}>
                  {t("cartSidebar.partnerSaving", { partnerName })}
                </Typography>
              </Box>
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}
              >
                {`− ${formatPrice(partnerSaving)}`}
              </Typography>
            </Box>
          </Box>
          <Divider />
        </>
      )}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 1 }}>
        <Typography variant="h6" component="p">
          {t("cartSidebar.total")}
        </Typography>
        <Typography variant="h6" component="p" sx={{ fontVariantNumeric: "tabular-nums" }}>
          {formatPrice(totalPrice)}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Button
          variant="contained"
          fullWidth={true}
          href={checkoutUrl}
          data-amp-default-track={true}
          data-amp-event-type="cart.button.checkout"
        >
          {t("cart.checkout")}
        </Button>
        <Button
          variant="contained"
          color="secondary"
          fullWidth={true}
          onClick={handleShopMore}
          data-amp-default-track={true}
          data-amp-event-type="cart.button.shopMore"
        >
          {t("cartSidebar.shopMore")}
        </Button>
      </Box>
    </Box>
  );
};

export default CartFooter;
